import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  Cake,
  CalendarMonth,
  Celebration,
  Description,
  EmojiPeople,
  EventAvailable,
  Notifications,
  Payments,
  Today,
} from '@mui/icons-material';
import { dashboardApi, DashboardCelebrationsData } from '../api/dashboardApi';
import { employeeApi } from '../api/employeeApi';
import { attendanceApi } from '../api/attendanceApi';
import { leaveApi } from '../api/leaveApi';
import { payrollApi } from '../api/payrollApi';
import { notificationApi } from '../api/notificationApi';
import WebPunchCard from '../components/Dashboard/WebPunchCard';
import { LeaveBalanceDto, LeaveRequestDto, NotificationItem, HolidayDto } from '../types';
import { PayslipDto } from '../types/payrollTypes';
import { RootState } from '../store/store';

type EmployeeDashboardData = {
  employeeName: string;
  joinedDate?: string | null;
  todayPunches: any[];
  leaveBalance: LeaveBalanceDto[];
  leaveRequests: LeaveRequestDto[];
  payslips: PayslipDto[];
  notifications: NotificationItem[];
  upcomingHolidays: HolidayDto[];
};

const EmployeeDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useSelector((state: RootState) => state.auth.user?.permissions ?? []);
  const [data, setData] = useState<EmployeeDashboardData | null>(null);
  const [celebrations, setCelebrations] = useState<DashboardCelebrationsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        const currentEmployee = await employeeApi.getCurrentEmployee();
        const currentYear = new Date().getFullYear();
        const canViewNotifications = permissions.includes('ViewNotifications');
        const canViewMyPayslips = permissions.includes('ViewMyPayslip');

        const results = await Promise.allSettled([
          attendanceApi.getTodayStatus(currentEmployee.id),
          leaveApi.getLeaveBalance({ employeeId: currentEmployee.id }),
          leaveApi.getLeaveRequests({ employeeId: currentEmployee.id }),
          canViewMyPayslips ? payrollApi.getMyPayslips() : Promise.resolve([] as PayslipDto[]),
          canViewNotifications
            ? notificationApi.getMyNotifications({ pageNumber: 1, pageSize: 5 })
            : Promise.resolve({ totalCount: 0, unreadCount: 0, items: [] as NotificationItem[] }),
          leaveApi.getHolidays({ year: currentYear }),
          dashboardApi.getCelebrations(),
        ]);

        const [todayStatus, leaveBalance, leaveRequests, payslips, notificationsResponse, holidays, celebrationsResponse] = results;

        const getValue = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
          result.status === 'fulfilled' ? result.value : fallback;

        setData({
          employeeName: currentEmployee.fullName,
          joinedDate: currentEmployee.joinedDate,
          todayPunches: getValue(todayStatus, { isCheckedIn: false, todayPunches: [] as any[] }).todayPunches ?? [],
          leaveBalance: getValue(leaveBalance, [] as LeaveBalanceDto[]),
          leaveRequests: getValue(leaveRequests, [] as LeaveRequestDto[]).slice(0, 5),
          payslips: getValue(payslips, [] as PayslipDto[]).slice(0, 3),
          notifications: getValue(
            notificationsResponse,
            { totalCount: 0, unreadCount: 0, items: [] as NotificationItem[] }
          ).items ?? [],
          upcomingHolidays: getValue(holidays, [] as HolidayDto[])
            .filter((holiday) => new Date(holiday.date) >= new Date(new Date().toDateString()))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .slice(0, 5),
        });
        setCelebrations(getValue(celebrationsResponse, { birthdays: [], anniversaries: [], newJoiners: [] }));
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load employee dashboard');
      } finally {
        setLoading(false);
      }
    };

    void loadDashboard();
  }, [permissions]);

  const totalLeaveBalance = useMemo(
    () => data?.leaveBalance.reduce((total, item) => total + item.remainingDays, 0) ?? 0,
    [data]
  );

  const unreadNotifications = useMemo(
    () => data?.notifications.filter((item) => !item.isRead).length ?? 0,
    [data]
  );

  const latestPunch = useMemo(() => {
    if (!data?.todayPunches?.length) return null;

    const sorted = [...data.todayPunches].sort((a, b) => {
      const aTime = new Date(a.time || a.punchTime || a.createdAt || 0).getTime();
      const bTime = new Date(b.time || b.punchTime || b.createdAt || 0).getTime();
      return bTime - aTime;
    });

    return sorted[0];
  }, [data]);

  const statCards = [
    {
      title: 'Today Punches',
      value: data?.todayPunches.length ?? 0,
      icon: <Today sx={{ fontSize: 28 }} />,
      color: '#1976d2',
    },
    {
      title: 'Leave Balance',
      value: totalLeaveBalance,
      icon: <EventAvailable sx={{ fontSize: 28 }} />,
      color: '#2e7d32',
    },
    {
      title: 'Unread Notifications',
      value: unreadNotifications,
      icon: <Notifications sx={{ fontSize: 28 }} />,
      color: '#ed6c02',
    },
    {
      title: 'Payslips',
      value: data?.payslips.length ?? 0,
      icon: <Payments sx={{ fontSize: 28 }} />,
      color: '#6a1b9a',
    },
  ];

  const celebrationCardSx = {
    p: 3,
    borderRadius: 3,
    height: 320,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as const;

  // const formatDate = (value?: string | null) =>
  //   value
  //     ? new Date(value).toLocaleDateString('en-IN', {
  //         day: '2-digit',
  //         month: 'short',
  //         year: 'numeric',
  //       })
  //     : '-';

  // const formatDateTime = (value?: string | null) =>
  //   value
  //     ? new Date(value).toLocaleString('en-IN', {
  //         day: '2-digit',
  //         month: 'short',
  //         hour: '2-digit',
  //         minute: '2-digit',
  //       })
  //     : '-';

  const formatDate = (value?: string | null) =>
  value && !isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '-';

const formatDateTime = (value?: string | null) =>
  value && !isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',      // ✅ added
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,         // ✅ ensures AM/PM
      })
    : '-';

    const dateTime =
  latestPunch?.date && latestPunch?.time
    ? `${latestPunch.date}T${latestPunch.time}`
    : latestPunch?.punchTime || latestPunch?.createdAt;

  const getLeaveStatusLabel = (status: number) => {
    switch (status) {
      case 1:
        return 'Approved';
      case 2:
        return 'Rejected';
      case 3:
        return 'Cancelled';
      default:
        return 'Pending';
    }
  };

  //const getPunchLabel = (punch: any) => punch?.type || punch?.punchType || punch?.status || 'Punch';

      const getPunchLabel = (type?: string) => {
        if (!type) return 'Punch';

        return type
          .toLowerCase()
          .replace('_', ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
      };

  const renderCelebrationList = (
    items: Array<{
      employeeId: string;
      fullName: string;
      employeeCode: string;
      departmentName?: string | null;
      designationName?: string | null;
    }>,
    emptyMessage: string,
    meta?: (item: any) => string
  ) => {
    if (!items.length) {
      return (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center' }}>
          <Typography color="text.secondary">{emptyMessage}</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 1,
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 3 },
        }}
      >
        <Stack spacing={1.25}>
        {items.map((item) => (
          <Box key={item.employeeId} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
            <Typography variant="body2" fontWeight={700}>
              {item.fullName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.employeeCode}
              {item.designationName ? ` • ${item.designationName}` : ''}
              {item.departmentName ? ` • ${item.departmentName}` : ''}
            </Typography>
            {meta && (
              <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', mt: 0.25 }}>
                {meta(item)}
              </Typography>
            )}
          </Box>
        ))}
        </Stack>
      </Box>
    );
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid size={{ xs: 12, md: index === 0 ? 12 : 6 }} key={index}>
            <Skeleton variant="rounded" height={index === 0 ? 120 : 260} />
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Welcome back, {data?.employeeName?.split(' ')[0] || 'Employee'}
        </Typography>
        <Typography color="text.secondary" sx={{mb: 5}}>
          {/* Your dashboard shows only your own attendance, leave, payroll, and notifications. */}
        </Typography>
        {data?.joinedDate && (
          <Chip
            sx={{ mt: 1.5 }}
            icon={<CalendarMonth sx={{ fontSize: 16 }} />}
            label={`Joined on ${formatDate(data.joinedDate)}`}
            variant="outlined"
          />
        )}
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 3, alignItems: 'stretch' }}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <WebPunchCard />
        </Grid>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Grid container spacing={2.5} sx={{ height: '100%' }}>
            {statCards.map((card) => (
              <Grid size={{ xs: 12, sm: 6 }} key={card.title}>
                <Card
                  sx={{
                    height: '100%',
                    minHeight: 152,
                    borderRadius: 3,
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <CardContent sx={{ width: '100%', p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                      <Box>
                        <Typography variant="h4" fontWeight={800} lineHeight={1}>
                          {card.value}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
                          {card.title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          color: card.color,
                          bgcolor: `${card.color}14`,
                          borderRadius: 3,
                          width: 52,
                          height: 52,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
      {/* Birthdays */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={celebrationCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
              <Cake color="secondary" />
              <Typography fontWeight={600}>Birthdays</Typography>
              <Chip size="small" label={celebrations?.birthdays.length ?? 0} />
            </Box>

            {renderCelebrationList(
              celebrations?.birthdays ?? [],
              'No birthdays today.',
              (item) =>
                `DOB: ${new Date(item.dateOfBirth).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                })}`
            )}
          </Paper>
        </Grid>

        {/* Work Anniversaries */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={celebrationCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
              <Celebration color="primary" />
              <Typography fontWeight={600}>Work Anniversaries</Typography>
              <Chip size="small" label={celebrations?.anniversaries.length ?? 0} />
            </Box>

            {renderCelebrationList(
              celebrations?.anniversaries ?? [],
              'No work anniversaries today.',
              (item) =>
                `${item.yearsCompleted} year${
                  item.yearsCompleted === 1 ? '' : 's'
                } completed`
            )}
          </Paper>
        </Grid>

        {/* New Joiners */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={celebrationCardSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexShrink: 0 }}>
              <EmojiPeople sx={{ color: '#00bcd4' }} />
              <Typography fontWeight={600}>New Joiners</Typography>
              <Chip size="small" label={celebrations?.newJoiners.length ?? 0} />
            </Box>

            {renderCelebrationList(
              celebrations?.newJoiners ?? [],
              'No new joiners in the last 7 days.',
              (item) =>
                `Joined: ${new Date(item.joinedDate).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}`
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Today Summary
              </Typography>
              {/* <Button size="small" onClick={() => navigate('/attendance')}>
                Open Attendance
              </Button> */}
            </Stack>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Latest Punch
                </Typography>
                {/* <Typography variant="body1" fontWeight={600}>
                  {latestPunch
                    ? `${getPunchLabel(latestPunch.type)} at ${formatDateTime(latestPunch.time || latestPunch.punchTime || latestPunch.createdAt)}`
                    : 'No punch recorded today'}
                </Typography> */}
              <Typography variant="body1" fontWeight={600}>
                {latestPunch
                  ? `${getPunchLabel(latestPunch.type)} at ${formatDateTime(dateTime)}`
                  : 'No punch recorded today'}
              </Typography>
              </Box>
              {/* <Divider /> */}
              {/* <Box>
                <Typography variant="body2" color="text.secondary">
                  Quick Access
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                  <Chip label="My Attendance" onClick={() => navigate('/attendance')} clickable />
                  <Chip label="My Leave" onClick={() => navigate('/leave/my-leave')} clickable />
                  <Chip label="My Payslips" onClick={() => navigate('/payroll/my-payslips')} clickable />
                  <Chip label="Notifications" onClick={() => navigate('/notifications')} clickable />
                </Stack>
              </Box> */}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Leave Balance
              </Typography>
              <Button size="small" onClick={() => navigate('/leave/my-leave')}>
                Manage Leave
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              {data?.leaveBalance.length ? (
                data.leaveBalance.map((item) => (
                  <Box key={item.leaveTypeId}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.leaveTypeName}
                      </Typography>
                      <Chip
                        label={`${item.remainingDays} days left`}
                        size="small"
                        sx={{
                          bgcolor: item.leaveTypeColor || 'rgba(25, 118, 210, 0.12)',
                          color: item.leaveTypeColor ? '#fff' : 'primary.main',
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Used {item.usedDays} of {item.totalDays + item.carryForwardDays} days
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No leave balance available yet.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Recent Leave Requests
              </Typography>
              <Button size="small" onClick={() => navigate('/leave/my-leave')}>
                View All
              </Button>
            </Stack>
            <List disablePadding>
              {data?.leaveRequests.length ? (
                data.leaveRequests.map((request) => (
                  <ListItem key={request.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton sx={{ borderRadius: 2 }}>
                      <ListItemText
                        primary={`${request.leaveTypeName} • ${request.numberOfDays} day(s)`}
                        secondary={`${formatDate(request.startDate)} to ${formatDate(request.endDate)}`}
                      />
                      <Chip label={getLeaveStatusLabel(request.status)} size="small" />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <Typography color="text.secondary">No leave requests found.</Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Payroll Snapshot
              </Typography>
              <Button size="small" onClick={() => navigate('/payroll/my-payslips')}>
                Open Payroll
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              {data?.payslips.length ? (
                data.payslips.map((payslip) => (
                  <Box key={payslip.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography fontWeight={600}>
                        {new Date(payslip.year, payslip.month - 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generated {formatDateTime(payslip.generatedAt)}
                      </Typography>
                    </Box>
                    <Description color="action" />
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No payslips available yet.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Notifications
              </Typography>
              <Button size="small" onClick={() => navigate('/notifications')}>
                View All
              </Button>
            </Stack>
            <List disablePadding>
              {data?.notifications.length ? (
                data.notifications.map((item) => (
                  <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton sx={{ borderRadius: 2 }}>
                      <ListItemText
                        primary={item.title}
                        secondary={item.message}
                        primaryTypographyProps={{ fontWeight: item.isRead ? 500 : 700 }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <Typography color="text.secondary">No notifications available.</Typography>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                Upcoming Holidays
              </Typography>
              <AccessTime color="action" />
            </Stack>
            <Stack spacing={1.5}>
              {data?.upcomingHolidays.length ? (
                data.upcomingHolidays.map((holiday) => (
                  <Box key={holiday.id}>
                    <Typography fontWeight={600}>{holiday.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(holiday.date)}
                      {holiday.description ? ` • ${holiday.description}` : ''}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">No upcoming holidays found.</Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmployeeDashboardPage;
